import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Animator from 'src/lib/Animator';
import { parseAmount } from 'src/lib/utils';
import { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView, ImageView, View } from '@play-co/timestep-core/ui';
import { createEmitter } from 'src/lib/Emitter';
import { Match3 } from 'src/replicant/minigame/match3';
import StateObserver from 'src/StateObserver';
import { CircularProgressBar } from './shared/CircularProgressBar';
import statePromise from 'src/lib/statePromise';
import { duration } from 'src/replicant/utils/duration';
import { isSceneEntering, isSceneLeaving } from '../../lib/stateUtils';

namespace style {
  const pointsLayout = {
    width: 300,
    height: 87,
  };
  export const points = {
    layout: {
      ...pointsLayout,
      x: 197,
      y: 53,
    },
    frame: {
      ...pointsLayout,
      image: 'assets/minigame/minigame_currency_panel.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 34, right: 34 },
        vertical: { top: 34, bottom: 39 },
      },
    },
    icon: {
      ...pointsLayout,
      image: 'assets/minigame/score_icon_yellow.png',
      width: 80,
      x: 10,
    },
    label: {
      ...pointsLayout,
      x: 90,
      width: 180,
      align: 'right',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      font: 'Body',
      size: 46,
    },
  } as const;

  const gemsLayout = {
    ...pointsLayout,
    width: 172,
  };
  export const gems = {
    layout: {
      ...gemsLayout,
      y: points.layout.y,
      x: 538,
    },
    frame: {
      ...points.frame,
      ...gemsLayout,
    },
    icon: {
      ...gemsLayout,
      width: 60,
      x: 10,
      image: 'assets/ui/hud/icons/hud_gem.png',
      scaleMethod: 'contain',
    },
    label: {
      ...points.label,
      ...gemsLayout,
      size: 40,
      x: 80,
      width: 75,
    },
  } as const;

  export const timer = {
    progress: {
      x: 10,
      y: 25,
      scale: 1.5,
    },
    label: {
      ...points.label,
      x: 48,
      y: 48,
      width: 80,
      height: 80,
      centerOnOrigin: true,
      align: 'center',
      size: 30,
    },
  } as const;
}

const { puzzleDuration } = Match3.ruleset;

export class MinigameHud {
  readonly view: View;
  private timerProgress: CircularProgressBar;
  private timer: LangBitmapFontTextView;
  private points: LangBitmapFontTextView;
  private gems: LangBitmapFontTextView;

  private pointsAnimator = new Animator((value) => {
    this.points.localeText = () => parseAmount(value);
  });

  private gemsAnimator = new Animator((value) => {
    this.gems.localeText = () => `x${parseAmount(value)}`;
  });

  private timerAnimator = new Animator((value) => {
    this.timer.localeText = () => `${Math.ceil(value / 1000)}`;
    this.timerProgress.progress = value / puzzleDuration;
  }, puzzleDuration);

  constructor(opts: ViewOpts) {
    this.view = new View(opts);

    this.timerProgress = new CircularProgressBar({
      superview: this.view,
      frameStyle: style.timer.progress,
      progress: 1,
    });

    this.timer = this.timerProgress.addSubview(
      new LangBitmapFontTextView({
        ...style.timer.label,
        localeText: () => `${Math.ceil(puzzleDuration / 1000)}`,
      }),
    );

    this.points = this.createPoints();
    this.gems = this.createGems();

    // Update total rewards after each turn
    createEmitter(this.view, ({ user }) =>
      Match3.getters(user).getTurnCount(),
    ).addListener(() => {
      const { user } = StateObserver.getState();
      const { gems, points } = Match3.getters(user).getTotalRewards();
      void this.gemsAnimator.setTarget(gems);
      void this.pointsAnimator.setTarget(points);
    });

    createEmitter(this.view, ({ match3 }) => match3.readyState).addListener(
      async (readyState) => {
        if (readyState === 'go') {
          await statePromise(({ match3 }) => match3.readyState === 'hidden');
          const tenSeconds = duration({ seconds: 10 });
          await this.timerAnimator.setTarget(
            tenSeconds,
            puzzleDuration - tenSeconds,
          );
          this.timerProgress.updateFillOpts({
            image: 'assets/ui/hud/meters/level_fill_warning.png',
          });
          this.timer.updateOpts({ color: '#ED7A92' });
          await this.timerAnimator.setTarget(0, tenSeconds);
        }
      },
    );

    createEmitter(this.view, () => isSceneEntering('minigame')).addListener(
      () => {
        void this.timerAnimator.setTarget(puzzleDuration);
        this.timerProgress.updateFillOpts({
          image: 'assets/ui/hud/meters/level_fill.png',
        });
        this.timer.updateOpts({ color: style.timer.label.color });
      },
    );
  }

  private createPoints(): LangBitmapFontTextView {
    const layout = this.view.addSubview(new View(style.points.layout));

    layout.addSubview(new ImageScaleView(style.points.frame));

    layout.addSubview(new ImageView(style.points.icon));

    return layout.addSubview(new LangBitmapFontTextView(style.points.label));
  }

  private createGems(): LangBitmapFontTextView {
    const layout = this.view.addSubview(new View(style.gems.layout));

    layout.addSubview(new ImageScaleView(style.gems.frame));

    layout.addSubview(new ImageScaleView(style.gems.icon));

    return layout.addSubview(new LangBitmapFontTextView(style.gems.label));
  }

  layout() {
    const { height } = this.view.style;
    this.timerProgress.style.y = height / 2 - 75;

    const pointsLayout = this.points.getSuperview();
    pointsLayout.style.y = height / 2 - 44;
    this.gems.getSuperview().style.y = pointsLayout.style.y;
  }
}
